import { memo, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { SchedulingOutcomeAggregate } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { DemoChip } from '@frontend/analytics-shared';
import { SchedulingChip, useCallIntelDefaultsShallowStore } from '@frontend/call-intel-core';
import { Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { CustomTooltipTitle } from '../custom-tooltip-title';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelShallowStore } from '../hooks/use-call-intel-store';
import { OpportunityLegendContent } from '../opportunity-legend-content';

/**
 * @deprecated TODO: Remove this type when TSS Multi changes are live
 */
type Props = {
  data?: CallIntelTypes.GetOverviewStatsIO['output'];
  isLoading?: boolean;
};

/**
 * @deprecated TODO: Remove this type when TSS Multi changes are live
 */
export const OverviewSummaryOpportunitiesStack = memo(({ data, isLoading }: Props) => {
  const { t } = useTranslation('analytics');
  const { showDemoChipAndBanner } = useCallIntelDemoFlags();
  const { dataLabels } = useCallIntelDefaultsShallowStore('dataLabels');
  const { subView } = useCallIntelShallowStore('subView');
  const { filterHintText } = useActiveFilters();

  const chartTitle = useMemo(() => {
    return t('{{name}} Opportunities', {
      name:
        subView.type === 'sentiment'
          ? dataLabels.sentiments?.[subView.id || '']
          : subView.type === 'appointment-type'
          ? dataLabels.appointmentTypes?.[subView.id || '']
          : dataLabels.categories?.[subView.id || ''],
    });
  }, [dataLabels.appointmentTypes, dataLabels.categories, dataLabels.sentiments, subView.id, subView.type, t]);

  const chartData = useMemo(() => {
    const scheduled = data?.summary?.scheduledOpportunities || 0;
    const unscheduled = (data?.summary?.totalOpportunities || 0) - (data?.summary?.scheduledOpportunities || 0);
    const noOpportunity = data ? (data?.totalCallsAnalyzed || 0) - (data?.summary?.totalOpportunities || 0) : 0;

    return {
      data: {
        groups: [
          {
            name: 'opportunities',
            values: {
              scheduled,
              unscheduled,
              noOpportunity,
            },
          },
        ],
      },
      maxValue: scheduled + unscheduled + noOpportunity,
      noOpportunity,
      scheduled,
      unscheduled,
    };
  }, [data]);

  const legendContentProps = useCallback((legendId?: string | null) => {
    const iconProps = {
      iconName: 'check-small',
      label: t('Scheduled'),
      variant: 'success',
    };

    if (legendId === 'unscheduled') {
      iconProps.iconName = 'x-small';
      iconProps.label = t('Unscheduled');
      iconProps.variant = 'warn';
    } else if (legendId === 'noOpportunity') {
      iconProps.iconName = 'feedback-bad-small';
      iconProps.label = t('No Opportunity');
      iconProps.variant = 'neutral';
    }

    return iconProps as {
      iconName: IconName;
      label: string;
      variant: 'success' | 'warn' | 'neutral';
    };
  }, []);

  return (
    <Chart
      colors={{
        noOpportunity: theme.colors.neutral50,
        scheduled: theme.colors.success40,
        unscheduled: theme.colors.warning50,
      }}
      commonTooltipLabel={t('Calls')}
      isLoading={isLoading}
      labels={{
        noOpportunity: t('No Opportunity'),
        scheduled: t('Scheduled'),
        unscheduled: t('Unscheduled'),
      }}
    >
      <Chart.Header
        leftElement={showDemoChipAndBanner ? <DemoChip /> : null}
        subtitle={filterHintText}
        title={chartTitle}
      />
      <Chart.BarChart
        appearance={{
          barSize: 40,
          customTooltipTitle: ({ hoveredSegment }) => {
            const iconProps = legendContentProps(hoveredSegment);
            return (
              <>
                <OpportunityLegendContent
                  iconName={iconProps.iconName}
                  label={iconProps.label}
                  variant={iconProps.variant}
                />
                <CustomTooltipTitle label={''} />
              </>
            );
          },
          groupsGap: 0,
          layout: 'vertical',
          maxValue: chartData.maxValue,
          mode: 'stacked',
          showScrollControls: false,
        }}
        data={chartData.data}
        formatValue={formatters.value}
      />
      {!!chartData.maxValue && (
        <Chart.Legends
          customData={{
            scheduled: {
              label: <OpportunityLegendContent iconName='check-small' label={t('Scheduled')} variant='success' />,
              value: formatters.value(chartData.scheduled),
            },
            unscheduled: {
              label: <OpportunityLegendContent iconName='x-small' label={t('Unscheduled')} variant='warn' />,
              value: formatters.value(chartData.unscheduled),
            },
            noOpportunity: {
              label: (
                <OpportunityLegendContent iconName='feedback-bad-small' label={t('No Opportunity')} variant='neutral' />
              ),
              value: formatters.value(chartData.noOpportunity),
            },
          }}
          style={{ marginTop: theme.spacing(2) }}
        />
      )}
    </Chart>
  );
});

OverviewSummaryOpportunitiesStack.displayName = 'OverviewSummaryOpportunitiesStack';

type PropsV2 = {
  data?: SchedulingOutcomeAggregate;
  isLoading?: boolean;
};

export const OverviewSummaryOpportunitiesStackV2 = memo(({ data, isLoading }: PropsV2) => {
  const { t } = useTranslation('analytics');
  const { showDemoChipAndBanner } = useCallIntelDemoFlags();
  const { dataLabels, dataColors } = useCallIntelDefaultsShallowStore('dataLabels', 'dataColors');
  const { subView } = useCallIntelShallowStore('subView');
  const { filterHintText } = useActiveFilters();

  const chartTitle = useMemo(() => {
    const subViewLabel =
      subView.type === 'sentiment'
        ? dataLabels.sentiments?.[subView.id || '']
        : subView.type === 'appointment-type'
        ? dataLabels.appointmentTypes?.[subView.id || '']
        : dataLabels.categories?.[subView.id || ''];
    return t('{{name}} Calls - Scheduling Outcomes', { name: subViewLabel });
  }, [dataLabels, subView, t]);

  const { colors, chartLabels, tooltipLabels } = useMemo(() => {
    return {
      colors: dataColors.schedulingOutcomeV2 ?? {},
      chartLabels: dataLabels.schedulingOutcomeV2Alternate ?? {},
      tooltipLabels: dataLabels.schedulingOutcomeV2 ?? {},
    };
  }, [dataColors, dataLabels]);

  const chartData = useMemo(() => {
    const {
      scheduledOnCallCount = 0,
      scheduledPostCallCount = 0,
      stillUnscheduledCount = 0,
      noOpportunitiesCount = 0,
    } = data ?? {};
    const values = {
      [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_ON_CALL]: scheduledOnCallCount,
      [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_UNSCHEDULED]: stillUnscheduledCount,
      [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_SCHEDULED_POST_CALL]: scheduledPostCallCount,
      [CallIntelTypes.SchedulingOutcomeV2Enum.SCHEDULING_OUTCOME_NO_OPPORTUNITY]: noOpportunitiesCount,
    };
    return {
      data: {
        groups: [
          {
            name: 'opportunities',
            values,
          },
        ],
      },
      maxValue: data?.scheduledOpportunitiesCount,
      values,
    };
  }, [data]);

  const legendCustomData = useMemo(
    () =>
      (Object.keys(chartData.values) as Array<keyof typeof chartData.values>).reduce((acc, outcome) => {
        acc[outcome] = {
          label: <SchedulingChip schedulingOutcome={outcome} showTooltip={false} />,
          value: formatters.value(chartData.values[outcome]),
        };
        return acc;
      }, {} as Partial<Record<CallIntelTypes.SchedulingOutcomeV2Enum, { label: React.ReactNode; value: string }>>),
    [chartData]
  );

  const renderCustomTooltipTitle = useCallback(
    ({ hoveredSegment }: { hoveredSegment?: string | null }) => (
      <CustomTooltipTitle label={hoveredSegment != null ? tooltipLabels[hoveredSegment] : ''} />
    ),
    [tooltipLabels]
  );

  return (
    <Chart
      colors={colors}
      isLoading={isLoading}
      labels={chartLabels}
      style={{ marginBottom: 0 }}
      css={styles.chartStyles}
    >
      <Chart.HeaderV2
        title={
          <Chart.HeaderV2.Title leftElement={showDemoChipAndBanner ? <DemoChip /> : null}>
            {chartTitle}
          </Chart.HeaderV2.Title>
        }
        action={<Chart.HeaderV2.FilterText filterHintText={filterHintText} />}
      />
      <Chart.BarChart
        appearance={{
          barSize: 32,
          customTooltipTitle: renderCustomTooltipTitle,
          groupsGap: 0,
          layout: 'vertical',
          maxValue: chartData.maxValue,
          mode: 'stacked',
          showScrollControls: false,
        }}
        data={chartData.data}
        formatValue={formatters.value}
      />
      {!!chartData.maxValue && <Chart.Legends customData={legendCustomData} style={{ marginTop: theme.spacing(2) }} />}
    </Chart>
  );
});

OverviewSummaryOpportunitiesStackV2.displayName = 'OverviewSummaryOpportunitiesStackV2';

const styles = {
  chartStyles: css`
    .chart-content {
      padding: ${theme.spacing(3)};
    }
  `,
};
